/**
 * Length rules for the written (essay / email) questions of the English
 * assessments. The rule comes from the question itself
 * (`question_options.allowed_sentences`), then from a "(3-5 sentences)" style
 * directive in the question text, then from legacy per-position defaults.
 */

export type LimitKind = "sentences" | "words";

export interface WrittenQuestionLike {
  question_text?: string | null;
  question_options?: {
    allowed_sentences?: number[] | null;
    allowed_characters?: number[] | null;
  } | null;
}

export interface WrittenLimits {
  kind: LimitKind;
  min: number;
  max: number;
}

export interface WrittenValidation extends WrittenLimits {
  valid: boolean;
  count: number;
}

export function countSentences(text: string): number {
  return text
    .split(/[.!?]+(?:\s+|$)/)
    .filter((s: string) => s.trim().length > 0).length;
}

export function countWords(text: string): number {
  return text.split(/\s+/).filter((w: string) => w.length > 0).length;
}

export function parseDirective(
  text: string
): null | { type: LimitKind; min: number; max: number } {
  const paren = /\(([^)]*)\)/.exec(text)?.[1] || "";
  const lower = paren.toLowerCase();
  const sRange = lower.match(/(\d+)\s*-\s*(\d+)\s*sentences?/);
  if (sRange) {
    const a = parseInt(sRange[1], 10);
    const b = parseInt(sRange[2], 10);
    const min = Math.min(a, b) || 1;
    const max = Math.max(a, b) || min;
    return { type: "sentences", min, max };
  }
  const sSingle = lower.match(/(\d+)\s*sentences?/);
  if (sSingle) {
    const n = parseInt(sSingle[1], 10) || 1;
    return { type: "sentences", min: n, max: n };
  }
  const wRange = lower.match(/(\d+)\s*-\s*(\d+)\s*words?/);
  if (wRange) {
    const a = parseInt(wRange[1], 10);
    const b = parseInt(wRange[2], 10);
    const min = Math.min(a, b) || 1;
    const max = Math.max(a, b) || min;
    return { type: "words", min, max };
  }
  const wSingle = lower.match(/(\d+)\s*words?/);
  if (wSingle) {
    const n = parseInt(wSingle[1], 10) || 1;
    return { type: "words", min: n, max: n };
  }
  return null;
}

/** Limits shown to the candidate next to the answer counter. */
export function resolveLimits(
  question: WrittenQuestionLike | null | undefined,
  questionNumber: number
): WrittenLimits {
  const sentences = question?.question_options?.allowed_sentences;
  if (sentences && sentences.length > 0) {
    return {
      kind: "sentences",
      min: sentences[0] || 1,
      max: sentences[1] || sentences[0] || 1
    };
  }
  const directive = parseDirective(String(question?.question_text || ""));
  if (directive) {
    return { kind: directive.type, min: directive.min, max: directive.max };
  }
  if (questionNumber === 2) return { kind: "words", min: 1, max: 2000 };
  if (questionNumber === 4) return { kind: "sentences", min: 4, max: 5 };

  const chars = question?.question_options?.allowed_characters || [];
  return {
    kind: "words",
    min: chars[0] || 0,
    max: chars[1] || chars[0] || 0
  };
}

/** Whether `text` satisfies the question's rule (drives the Next button). */
export function validateAnswer(
  text: string,
  question: WrittenQuestionLike | null | undefined,
  questionNumber: number
): WrittenValidation {
  const sentences = question?.question_options?.allowed_sentences;
  if (sentences && sentences.length > 0) {
    const min = sentences[0] || 1;
    const max = sentences[1] || min;
    const count = countSentences(text);
    return {
      kind: "sentences",
      min,
      max,
      count,
      valid: count >= min && count <= max
    };
  }
  const directive = parseDirective(String(question?.question_text || ""));
  if (directive) {
    const count =
      directive.type === "sentences" ? countSentences(text) : countWords(text);
    return {
      kind: directive.type,
      min: directive.min,
      max: directive.max,
      count,
      valid: count >= directive.min && count <= directive.max
    };
  }
  if (questionNumber === 2) {
    const count = countWords(text);
    return {
      kind: "words",
      min: 1,
      max: 2000,
      count,
      valid: count >= 1 && count <= 2000
    };
  }
  if (questionNumber === 4) {
    const count = countSentences(text);
    return {
      kind: "sentences",
      min: 4,
      max: 5,
      count,
      valid: count >= 4 && count <= 5
    };
  }
  const minWords = question?.question_options?.allowed_characters?.[0] || 1;
  const count = countWords(text);
  return {
    kind: "words",
    min: minWords,
    max: Infinity,
    count,
    valid: count >= minWords
  };
}

/**
 * Explains why Next is disabled. Empty when the answer is valid or nothing
 * has been typed yet.
 */
export function limitMessage(v: WrittenValidation): string {
  if (v.valid || v.count === 0) return "";
  const unit = (n: number) =>
    v.kind === "sentences"
      ? n === 1
        ? "sentence"
        : "sentences"
      : n === 1
      ? "word"
      : "words";
  const target =
    v.max === Infinity
      ? `at least ${v.min} ${unit(v.min)}`
      : v.min === v.max
      ? `exactly ${v.min} ${unit(v.min)}`
      : `${v.min} to ${v.max} ${unit(v.max)}`;
  return `Please write ${target}. You have written ${v.count} ${unit(
    v.count
  )}.`;
}
